// إشعارات الموبايل والتطبيق مقفول (0173، قرار المالك 03/10/2026: «أهم حاجة عندي الإشعارات تشتغل حتى لو التطبيق مش
// مفتوح قدامه، وجاله إشعار يفتح يشوف ده إيه»).
//
// داخل تطبيق أندرويد وحده: بعد الدخول يُطلب إذن الإشعارات (أندرويد 13+)، ويُسجَّل رمز Firebase عند الخادم، فيرسل إليه
// الخادم كل إشعارٍ ورسالة شات من نفس الإشارة التي ترسل لمتصفّح الكمبيوتر (0145). والضغط على الإشعار يفتح مكانه.
// في المتصفّح لا شيء هنا (Web Push له طريقه)، وتطبيقٌ قديم بلا الإضافة يُتجاوز بصمت — الكود يأتي من الخادم قبل الـAPK.
import { Capacitor } from '@capacitor/core';
import { post } from './api.ts';
import { isNativeApp } from './platform.ts';

const TOKEN_KEY = 'icontravel.fcmToken';
/**
 * نفس قناة الخادم (services/fcm.ts) — أهمية عالية: صوت وظهور فوق الشاشة.
 * **قناة جديدة باسمٍ جديد** (المالك 03/10/2026: «المفروض يكون في صوت على الموبايل مع الإشعار»): القناة الأولى
 * «icontravel» أُنشئت بـsound: 'default' والإضافة تقرؤه ملفًّا في res/raw فخرجت صامتة — وأندرويد لا يسمح بتعديل قناةٍ
 * بعد إنشائها. فالجديدة بلا sound (= نغمة إشعارات الموبايل)، والقديمة تُحذف.
 */
export const PUSH_CHANNEL = 'icontravel_alerts';
const OLD_CHANNELS = ['icontravel'];

let started = false;

const saved = (): string | null => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const remember = (t: string | null) => { try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* لا تخزين */ } };

/** يُستدعى بعد الدخول؛ آمنٌ للتكرار. يردّ حالة الإذن للعرض إن احتيج */
export async function startNativePush(): Promise<'granted' | 'denied' | 'unavailable'> {
  if (!isNativeApp() || !Capacitor.isPluginAvailable('PushNotifications')) return 'unavailable';
  const { PushNotifications } = await import('@capacitor/push-notifications');
  if (!started) {
    started = true;
    for (const id of OLD_CHANNELS) await PushNotifications.deleteChannel({ id }).catch(() => undefined);
    await PushNotifications.createChannel({
      id: PUSH_CHANNEL, name: 'إشعارات iConTravel', description: 'رسايل الفريق والتنبيهات وطلبات العملاء',
      importance: 5, visibility: 1, vibration: true, lights: true,
    }).catch(() => undefined);
    await PushNotifications.addListener('registration', t => {
      remember(t.value);
      void post('/me/fcm', { token: t.value, platform: 'android' }).catch(() => undefined);
    });
    // الضغط على الإشعار: يفتح مكانه (الشات مثلًا) — والإشعار العادي يفتح البرنامج والجرس فيه
    await PushNotifications.addListener('pushNotificationActionPerformed', a => {
      openFromPush((a.notification.data as { url?: string } | undefined)?.url);
    });
  }
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return 'denied';
  await PushNotifications.register();
  return 'granted';
}

/**
 * **الضغط على الإشعار لا يعيد تحميل البرنامج** (المراجعة الشاملة الثالثة «متوسط 3»، والمالك 03/10/2026: «موافق طبعا»).
 * كان `location.assign(url)` — و`/` من شاشةٍ عنوانها `/#bookings` تنقّلٌ كامل: البرنامج يتحمّل من الأول ويضيع حجزٌ نصّه
 * مكتوب. الآن يتغيّر التاب وحده (`#team-chat`) والشاشة المفتوحة تبقى، والإشعار العادي يكتفي بإظهار البرنامج.
 * ورابطٌ لغير البرنامج لا يُفتح أصلًا.
 */
export function openFromPush(url: string | undefined): void {
  if (!url || !url.startsWith('/') || url.startsWith('//')) return;
  const i = url.indexOf('#');
  if (i < 0) return;
  const hash = url.slice(i);
  if (window.location.hash !== hash) window.location.hash = hash;
}

/**
 * **العدد على أيقونة التطبيق يقلّ بما شوهد** (المالك 03/10/2026: «لما الموظف يفتح الإشعار ويشوفه المفروض إنه يختفي من
 * اللوجو من برّه… لما يشوف الإشعار العدد يقل»). أندرويد يعدّ ما في شريط الإشعارات — فيُزال منه كل إشعارٍ شوهد في البرنامج،
 * بوسمه (معرّف الإشعار، أو team-chat للشات): `keep(tag)` تقول ما يبقى. والضغط على الإشعار نفسه يزيله أصلًا.
 */
export async function syncDelivered(keep: (tag: string | undefined) => boolean): Promise<void> {
  if (!isNativeApp() || !Capacitor.isPluginAvailable('PushNotifications')) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  try {
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    const gone = notifications.filter(n => !keep(n.tag));
    if (gone.length) await PushNotifications.removeDeliveredNotifications({ notifications: gone });
  } catch { /* الشريط ليس شرطًا لشيء */ }
}

/** قبل الخروج: هذا الموبايل لا يستقبل إشعارات صاحبه بعده (يُنادى والجلسة ما زالت صالحة) */
export async function stopNativePush(): Promise<void> {
  const t = saved();
  if (!t) return;
  await post('/me/fcm/unregister', { token: t }).catch(() => undefined);
  remember(null);
}
